import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { formatCurrencySpaced } from '@/lib/market';
import DealsPage from '@/pages/fo/DealsPage';
import {
  Rocket, Building2, DollarSign, Landmark, ArrowUpRight,
  ArrowDownRight, TrendingUp, RefreshCw, Zap, Search, Layers, MoreHorizontal,
  AlertTriangle, Users, LayoutGrid, Home, Leaf, Music2, ChevronRight, Plus,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PM_PALETTE = ['#ffd879','#d9e2c3','#a8c5da','#c9a86b','#9b8dc4','#8dc4a8','#c4a88d','#8da8c4','#b8c4a8','#c4b88d','#c4c48d','#a8a8c4'];
const CHART_PALETTE = PM_PALETTE;

const fmt = (v, market) => formatCurrencySpaced(v, market);

const GainBadge = ({ pct }) => (
  <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${pct >= 0 ? 'text-[#ffd879]' : 'text-[#c97a3a]'}`}>
    {pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
    {Math.abs(pct).toFixed(1)}%
  </span>
);

const StatCard = ({ label, value, sub }) => (
  <div className="p-4 bg-white border border-[#e5e5e5] rounded-lg shadow-sm">
    <p className="text-xs text-[#4a4a4a] uppercase tracking-widest mb-1">{label}</p>
    <p className="text-lg font-bold text-[#1a1a1a]">{value}</p>
    {sub && <p className="text-xs text-[#4a4a4a] mt-0.5">{sub}</p>}
  </div>
);

const statusColors = {
  'Active':       'bg-emerald-100 text-emerald-700',
  'Investing':    'bg-emerald-100 text-emerald-700',
  'Harvesting':   'bg-[#ffd879]/20 text-[#1a1a1a]',
  'Pre-IPO':      'bg-[#ffd879]/20 text-[#1a1a1a]',
  'Partial Exit': 'bg-amber-100 text-amber-700',
  'Realized':     'bg-amber-100 text-amber-700',
  'Operating':    'bg-emerald-100 text-emerald-700',
  'Exited':       'bg-[#f9f9f9] text-[#4a4a4a]',
};

function EmptyState({ icon: Icon, label }) {
  return (
    <div className="text-center py-16">
      <Icon className="w-10 h-10 text-[#4a4a4a] mx-auto mb-3" />
      <p className="text-sm text-[#4a4a4a]">No {label} data yet</p>
    </div>
  );
}

// ── Shared card row ───────────────────────────────────────────────────────────
function FundCard({ icon: Icon, title, subtitle, metrics, status }) {
  return (
    <Card className="bg-white border-[#e5e5e5]">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#dae4c4] flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-[#1a1a1a]" />
            </div>
            <div>
              <p className="font-medium text-[#1a1a1a] text-sm">{title}</p>
              {subtitle && <p className="text-xs text-[#4a4a4a] mt-0.5">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-5 text-right shrink-0 flex-wrap justify-end">
            {metrics.map((m, i) => (
              <div key={i}>
                <p className="text-xs text-[#4a4a4a]">{m.label}</p>
                <p className={`text-sm font-bold ${m.color || 'text-[#1a1a1a]'}`}>{m.value}</p>
              </div>
            ))}
            {status && (
              <Badge className={`text-xs border-0 ${statusColors[status] || 'bg-[#f9f9f9] text-[#4a4a4a]'}`}>
                {status}
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Active (non-closed) pipeline deals matching a tab's category — surfaces new
// deals immediately, before they close into an actual holding. Deal creation
// itself only happens on the Deal Pipeline page; this is a read-only mirror.
function DealPipelineSection({ deals, categories, market }) {
  const items = (deals || []).filter(
    d => categories.includes(d.deal_category) && !['closed', 'passed', 'lost'].includes(d.stage)
  );
  if (!items.length) return null;
  return (
    <div className="mb-6">
      <p className="text-xs font-semibold text-[#4a4a4a] uppercase tracking-widest mb-3">Active Pipeline ({items.length})</p>
      <div className="space-y-3">
        {items.map(d => (
          <FundCard key={d.id} icon={Building2} title={d.deal_name || d.company_name}
            subtitle={[d.company_name, d.sector].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Stage', value: (d.stage || '').replace(/_/g, ' ') },
              { label: 'Target Investment', value: fmt(d.target_investment_amount, market) },
              ...(d.pre_money_valuation ? [{ label: 'Pre-Money', value: fmt(d.pre_money_valuation, market) }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

// ── Tab components ────────────────────────────────────────────────────────────

function EquityTab({ vc, pe, market, deals }) {
  const combined = [
    ...vc.map(d => ({ ...d, _cat: 'VC' })),
    ...pe.map(d => ({ ...d, _cat: 'PE' })),
  ];
  const pipeline = <DealPipelineSection deals={deals} categories={['vc', 'pe']} market={market} />;
  if (!combined.length) return <>{pipeline}<EmptyState icon={Building2} label="PE & VC funds" /></>;
  const totalCommitted = combined.reduce((s, d) => s + (d.committed || d.invested || 0), 0);
  const totalInvested  = combined.reduce((s, d) => s + (d.invested || 0), 0);
  const totalValue     = combined.reduce((s, d) => s + d.current_value, 0);
  const moicEntries    = combined.filter(d => d.moic != null);
  const avgMOIC        = moicEntries.length ? (moicEntries.reduce((s, d) => s + d.moic, 0) / moicEntries.length).toFixed(1) : '—';
  const irrEntries     = combined.filter(d => d.irr != null);
  const avgIRR         = irrEntries.length ? (irrEntries.reduce((s, d) => s + d.irr, 0) / irrEntries.length).toFixed(1) : null;

  return (
    <div className="space-y-6">
      {pipeline}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Committed"  value={fmt(totalCommitted, market)} sub={`${combined.length} funds`} />
        <StatCard label="Drawn Capital"    value={fmt(totalInvested, market)}  sub="deployed so far" />
        <StatCard label="Portfolio Value"  value={fmt(totalValue, market)}     sub="marked to NAV" />
        <StatCard label="Blended MOIC"     value={`${avgMOIC}x`}              sub={avgIRR ? `Avg IRR ${avgIRR}%` : 'across portfolio'} />
      </div>
      <div className="space-y-3">
        {combined.map((d, i) => (
          <FundCard
            key={i}
            icon={d._cat === 'VC' ? Rocket : Building2}
            title={<span className="flex items-center gap-2">{d.name}<span className="px-1.5 py-0.5 text-[10px] font-semibold bg-[#1a1a1a] text-[#ffd879]">{d._cat}</span></span>}
            subtitle={[d.manager || d.sector, d.strategy || d.stage || d.type, d.vintage && `Vintage ${d.vintage}`].filter(Boolean).join(' · ')}
            status={d.status}
            metrics={[
              { label: 'Committed',     value: fmt(d.committed || d.invested, market) },
              { label: 'Drawn / Invested', value: fmt(d.invested, market) },
              { label: 'Current Value', value: fmt(d.current_value, market), color: 'text-[#1a1a1a]' },
              ...(d.irr  != null ? [{ label: 'IRR',  value: `${d.irr}%`,  color: 'text-emerald-600' }] : []),
              ...(d.moic != null ? [{ label: 'MOIC', value: `${d.moic}x`, color: 'text-[#ffd879]' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function CreditTab({ data, market, deals }) {
  const pipeline = <DealPipelineSection deals={deals} categories={['credit']} market={market} />;
  if (!data.length) return <>{pipeline}<EmptyState icon={DollarSign} label="private credit" /></>;
  const totalInvested = data.reduce((s, d) => s + d.invested, 0);
  const totalValue    = data.reduce((s, d) => s + d.current_value, 0);
  const avgIRR        = (data.reduce((s, d) => s + d.irr, 0) / data.length).toFixed(1);
  return (
    <div className="space-y-6">
      {pipeline}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub={`${data.length} instruments`} />
        <StatCard label="Current Value"   value={fmt(totalValue, market)}    sub="marked to accrual" />
        <StatCard label="Avg. IRR"        value={`${avgIRR}%`}              sub="across portfolio" />
        <StatCard label="Unrealised Gain" value={fmt(totalValue - totalInvested, market)} sub="interest + appreciation" />
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={DollarSign} title={d.name} status={d.status}
            subtitle={`${d.type} · Vintage ${d.vintage} · ${d.tenure}`}
            metrics={[
              { label: 'Invested',      value: fmt(d.invested, market) },
              { label: 'Current Value', value: fmt(d.current_value, market) },
              { label: 'IRR',           value: `${d.irr}%`, color: 'text-emerald-600' },
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function InfraTab({ data, market, deals }) {
  const pipeline = <DealPipelineSection deals={deals} categories={['infra']} market={market} />;
  if (!data.length) return <>{pipeline}<EmptyState icon={Landmark} label="infrastructure" /></>;
  const totalInvested = data.reduce((s, d) => s + d.invested, 0);
  const totalValue    = data.reduce((s, d) => s + d.current_value, 0);
  const avgIRR        = (data.reduce((s, d) => s + d.irr, 0) / data.length).toFixed(1);
  const avgYield      = (data.reduce((s, d) => s + d.yield_pct, 0) / data.length).toFixed(1);
  return (
    <div className="space-y-6">
      {pipeline}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Invested"   value={fmt(totalInvested, market)} sub={`${data.length} projects`} />
        <StatCard label="Current Value"    value={fmt(totalValue, market)}    sub="marked to market" />
        <StatCard label="Blended IRR"      value={`${avgIRR}%`}              sub="net of fees" />
        <StatCard label="Avg. Cash Yield"  value={`${avgYield}%`}            sub="annual distributions" />
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={Landmark} title={d.name} status={d.status}
            subtitle={`${d.sector} · ${d.type} · Vintage ${d.vintage}`}
            metrics={[
              { label: 'Invested',      value: fmt(d.invested, market) },
              { label: 'Current Value', value: fmt(d.current_value, market) },
              { label: 'IRR',           value: `${d.irr}%`,      color: 'text-emerald-600' },
              { label: 'Yield',         value: `${d.yield_pct}%`, color: 'text-[#ffd879]' },
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function GenericFundTab({ data, market, icon: Icon, emptyLabel, category, deals }) {
  const pipeline = <DealPipelineSection deals={deals} categories={[category]} market={market} />;
  if (!data.length) return <>{pipeline}<EmptyState icon={Icon} label={emptyLabel} /></>;
  const totalInvested = data.reduce((s, d) => s + (d.invested || d.committed || 0), 0);
  const totalValue    = data.reduce((s, d) => s + (d.current_value || d.nav || 0), 0);
  const avgMOIC = data[0]?.moic != null ? (data.reduce((s, d) => s + (d.moic || 0), 0) / data.length).toFixed(2) : null;
  const avgIRR  = data[0]?.irr  != null ? (data.reduce((s, d) => s + (d.irr  || 0), 0) / data.length).toFixed(1) : null;
  return (
    <div className="space-y-6">
      {pipeline}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub={`${data.length} positions`} />
        <StatCard label="Current Value"   value={fmt(totalValue, market)}    sub="marked to market" />
        {avgIRR  && <StatCard label="Blended IRR"  value={`${avgIRR}%`}  sub="net of fees" />}
        {avgMOIC && <StatCard label="Blended MOIC" value={`${avgMOIC}x`} sub="across portfolio" />}
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={Icon} title={d.name} status={d.status}
            subtitle={[d.sector, d.type, d.manager, d.searcher, d.vintage && `Vintage ${d.vintage}`].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Invested',      value: fmt(d.invested || d.committed || 0, market) },
              { label: 'Current Value', value: fmt(d.current_value || d.nav || 0, market) },
              ...(d.irr  != null ? [{ label: 'IRR',  value: `${d.irr}%`,  color: 'text-emerald-600' }] : []),
              ...(d.moic != null ? [{ label: 'MOIC', value: `${d.moic}x`, color: 'text-[#ffd879]' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function HedgeFundTab({ data, market }) {
  if (!data.length) return <EmptyState icon={TrendingUp} label="hedge funds" />;
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const totalNav      = data.reduce((s, d) => s + (d.nav || 0), 0);
  const avgIRR        = data.filter(d => d.irr).length ? (data.reduce((s, d) => s + (d.irr || 0), 0) / data.length).toFixed(1) : null;
  const avgYTD        = data.filter(d => d.ytd).length ? (data.reduce((s, d) => s + (d.ytd || 0), 0) / data.length).toFixed(1) : null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Invested"   value={fmt(totalInvested, market)} sub={`${data.length} funds`} />
        <StatCard label="Current NAV"      value={fmt(totalNav, market)}      sub="marked to NAV" />
        {avgYTD && <StatCard label="Avg. YTD"       value={`${avgYTD}%`}             sub="year-to-date" />}
        {avgIRR && <StatCard label="Blended IRR"     value={`${avgIRR}%`}             sub="since inception" />}
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={TrendingUp} title={d.name} status={d.status}
            subtitle={[d.manager, d.strategy, d.lock_in && `Lock-in: ${d.lock_in}`].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Invested',  value: fmt(d.invested || d.aum_share || 0, market) },
              { label: 'NAV',       value: fmt(d.nav || 0, market), color: 'text-[#1a1a1a]' },
              ...(d.ytd  != null ? [{ label: 'YTD',  value: `${d.ytd}%`,  color: d.ytd >= 0 ? 'text-emerald-600' : 'text-[#c97a3a]' }] : []),
              ...(d.irr  != null ? [{ label: 'IRR',  value: `${d.irr}%`,  color: 'text-emerald-600' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function SecondariesTab({ data, market }) {
  if (!data.length) return <EmptyState icon={ChevronRight} label="secondary positions" />;
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const totalValue    = data.reduce((s, d) => s + (d.current_value || 0), 0);
  const avgMOIC = data.filter(d => d.moic).length ? (data.reduce((s, d) => s + (d.moic || 0), 0) / data.length).toFixed(2) : null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub={`${data.length} positions`} />
        <StatCard label="Current Value"   value={fmt(totalValue, market)}    sub="marked to NAV" />
        {avgMOIC && <StatCard label="Blended MOIC" value={`${avgMOIC}x`} sub="vs. purchase price" />}
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={Layers} title={d.name} status={d.status}
            subtitle={[d.original_fund, d.discount_to_nav != null && `Disc. ${d.discount_to_nav}% to NAV`, d.vintage && `Vintage ${d.vintage}`].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Purchase Price', value: fmt(d.invested || 0, market) },
              { label: 'Current Value',  value: fmt(d.current_value || 0, market) },
              ...(d.moic != null ? [{ label: 'MOIC', value: `${d.moic}x`, color: 'text-[#ffd879]' }] : []),
              ...(d.irr  != null ? [{ label: 'IRR',  value: `${d.irr}%`,  color: 'text-emerald-600' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function SpecialSituationsTab({ data, market }) {
  if (!data.length) return <EmptyState icon={AlertTriangle} label="special situations" />;
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const totalValue    = data.reduce((s, d) => s + (d.current_value || 0), 0);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub={`${data.length} positions`} />
        <StatCard label="Current Value"   value={fmt(totalValue, market)}    sub="marked to market" />
        <StatCard label="Unrealised P&L"  value={fmt(totalValue - totalInvested, market)} sub="appreciation" />
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={AlertTriangle} title={d.name} status={d.status}
            subtitle={[d.type, d.sector, d.vintage && `Vintage ${d.vintage}`].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Invested',      value: fmt(d.invested || 0, market) },
              { label: 'Current Value', value: fmt(d.current_value || 0, market) },
              ...(d.moic != null ? [{ label: 'MOIC', value: `${d.moic}x`, color: 'text-[#ffd879]' }] : []),
              ...(d.irr  != null ? [{ label: 'IRR',  value: `${d.irr}%`,  color: 'text-emerald-600' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function PreIPOTab({ data, market }) {
  if (!data.length) return <EmptyState icon={Rocket} label="pre-IPO positions" />;
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const totalValue    = data.reduce((s, d) => s + (d.current_value || 0), 0);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub={`${data.length} companies`} />
        <StatCard label="Current Value"   value={fmt(totalValue, market)}    sub="marked to last round" />
        <StatCard label="Unrealised Gain" value={fmt(totalValue - totalInvested, market)} sub="vs. cost basis" />
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={Rocket} title={d.name} status={d.status}
            subtitle={[d.company, d.sector, d.expected_ipo && `Expected IPO: ${d.expected_ipo}`].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Invested',      value: fmt(d.invested || 0, market) },
              { label: 'Current Value', value: fmt(d.current_value || 0, market) },
              ...(d.moic != null ? [{ label: 'MOIC', value: `${d.moic}x`, color: 'text-[#ffd879]' }] : []),
              ...(d.irr  != null ? [{ label: 'IRR',  value: `${d.irr}%`,  color: 'text-emerald-600' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function MezzanineTab({ data, market }) {
  if (!data.length) return <EmptyState icon={Layers} label="mezzanine positions" />;
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const totalValue    = data.reduce((s, d) => s + (d.current_value || 0), 0);
  const avgIRR        = data.filter(d => d.irr).length ? (data.reduce((s, d) => s + (d.irr || 0), 0) / data.length).toFixed(1) : null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub={`${data.length} instruments`} />
        <StatCard label="Current Value"   value={fmt(totalValue, market)}    sub="accrued + marked" />
        {avgIRR && <StatCard label="Blended IRR"  value={`${avgIRR}%`} sub="net of fees" />}
        <StatCard label="Gain"            value={fmt(totalValue - totalInvested, market)} sub="interest + kicker" />
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={Layers} title={d.name} status={d.status}
            subtitle={[d.issuer, d.coupon_rate && `Coupon ${d.coupon_rate}%`, d.maturity_date && `Maturity ${d.maturity_date}`, d.equity_kicker && 'Equity Kicker'].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Invested',      value: fmt(d.invested || 0, market) },
              { label: 'Current Value', value: fmt(d.current_value || 0, market) },
              ...(d.irr  != null ? [{ label: 'IRR',  value: `${d.irr}%`,  color: 'text-emerald-600' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function FoFTab({ data, market }) {
  if (!data.length) return <EmptyState icon={LayoutGrid} label="fund-of-funds" />;
  const totalCommitted = data.reduce((s, d) => s + (d.committed || d.invested || 0), 0);
  const totalNav       = data.reduce((s, d) => s + (d.nav || d.current_value || 0), 0);
  const avgMOIC = data.filter(d => d.moic).length ? (data.reduce((s, d) => s + (d.moic || 0), 0) / data.length).toFixed(2) : null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Committed" value={fmt(totalCommitted, market)} sub={`${data.length} FoF positions`} />
        <StatCard label="Current NAV"     value={fmt(totalNav, market)}       sub="blended NAV" />
        {avgMOIC && <StatCard label="Blended MOIC" value={`${avgMOIC}x`} sub="across portfolio" />}
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={LayoutGrid} title={d.name} status={d.status}
            subtitle={[d.manager, d.strategy, d.underlying_funds_count && `${d.underlying_funds_count} underlying funds`, d.vintage && `Vintage ${d.vintage}`].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Committed', value: fmt(d.committed || d.invested || 0, market) },
              { label: 'NAV',       value: fmt(d.nav || d.current_value || 0, market) },
              ...(d.moic != null ? [{ label: 'MOIC', value: `${d.moic}x`, color: 'text-[#ffd879]' }] : []),
              ...(d.irr  != null ? [{ label: 'IRR',  value: `${d.irr}%`,  color: 'text-emerald-600' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function CoInvestTab({ data, market }) {
  if (!data.length) return <EmptyState icon={Users} label="co-investments" />;
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const totalValue    = data.reduce((s, d) => s + (d.current_value || 0), 0);
  const avgMOIC = data.filter(d => d.moic).length ? (data.reduce((s, d) => s + (d.moic || 0), 0) / data.length).toFixed(2) : null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub={`${data.length} co-investments`} />
        <StatCard label="Current Value"   value={fmt(totalValue, market)}    sub="marked to last round" />
        {avgMOIC && <StatCard label="Blended MOIC" value={`${avgMOIC}x`} sub="across portfolio" />}
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={Users} title={d.name || d.company} status={d.status}
            subtitle={[d.lead_investor && `Lead: ${d.lead_investor}`, d.sector, d.stage, d.round_name].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Co-invested',   value: fmt(d.invested || 0, market) },
              { label: 'Current Value', value: fmt(d.current_value || 0, market) },
              ...(d.moic != null ? [{ label: 'MOIC', value: `${d.moic}x`, color: 'text-[#ffd879]' }] : []),
              ...(d.irr  != null ? [{ label: 'IRR',  value: `${d.irr}%`,  color: 'text-emerald-600' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function PrivateREITTab({ data, market }) {
  if (!data.length) return <EmptyState icon={Home} label="private REIT positions" />;
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const totalNav      = data.reduce((s, d) => s + (d.nav || d.current_value || 0), 0);
  const avgYield      = data.filter(d => d.yield_pct).length ? (data.reduce((s, d) => s + (d.yield_pct || 0), 0) / data.length).toFixed(1) : null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub={`${data.length} positions`} />
        <StatCard label="Current NAV"     value={fmt(totalNav, market)}      sub="marked to NAV" />
        {avgYield && <StatCard label="Avg. Yield" value={`${avgYield}%`} sub="distribution yield" />}
        <StatCard label="Unrealised Gain" value={fmt(totalNav - totalInvested, market)} sub="NAV vs. cost" />
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={Home} title={d.name} status={d.status}
            subtitle={[d.manager, d.property_type, d.distribution_frequency && `Dist: ${d.distribution_frequency}`].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Invested',   value: fmt(d.invested || 0, market) },
              { label: 'NAV',        value: fmt(d.nav || d.current_value || 0, market) },
              ...(d.yield_pct != null ? [{ label: 'Yield', value: `${d.yield_pct}%`, color: 'text-[#ffd879]' }] : []),
              ...(d.irr       != null ? [{ label: 'IRR',   value: `${d.irr}%`,       color: 'text-emerald-600' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function RoyaltyTab({ data, market }) {
  if (!data.length) return <EmptyState icon={DollarSign} label="royalty / revenue-based investments" />;
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const totalReceived = data.reduce((s, d) => s + (d.received || d.total_received || 0), 0);
  const avgIRR        = data.filter(d => d.irr).length ? (data.reduce((s, d) => s + (d.irr || 0), 0) / data.length).toFixed(1) : null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Deployed"   value={fmt(totalInvested, market)} sub={`${data.length} positions`} />
        <StatCard label="Total Received"   value={fmt(totalReceived, market)} sub="cash distributions" />
        {avgIRR && <StatCard label="Blended IRR" value={`${avgIRR}%`} sub="net of fees" />}
        <StatCard label="Remaining Balance" value={fmt(Math.max(0, totalInvested - totalReceived), market)} sub="yet to be recovered" />
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={DollarSign} title={d.name} status={d.status}
            subtitle={[d.company, d.revenue_share && `Rev-share: ${d.revenue_share}`, (d.maturity || d.maturity_date) && `Maturity ${d.maturity || d.maturity_date}`].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Deployed',  value: fmt(d.invested || 0, market) },
              { label: 'Received',  value: fmt(d.received || d.total_received || 0, market) },
              ...(d.irr != null ? [{ label: 'IRR', value: `${d.irr}%`, color: 'text-emerald-600' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

function NaturalResourcesTab({ data, market }) {
  if (!data.length) return <EmptyState icon={Leaf} label="natural resources" />;
  const totalInvested = data.reduce((s, d) => s + (d.invested || 0), 0);
  const totalValue    = data.reduce((s, d) => s + (d.current_value || 0), 0);
  const avgIRR        = data.filter(d => d.irr).length ? (data.reduce((s, d) => s + (d.irr || 0), 0) / data.length).toFixed(1) : null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Invested"  value={fmt(totalInvested, market)} sub={`${data.length} assets`} />
        <StatCard label="Current Value"   value={fmt(totalValue, market)}    sub="appraised value" />
        {avgIRR && <StatCard label="Blended IRR" value={`${avgIRR}%`} sub="since investment" />}
        <StatCard label="Unrealised Gain" value={fmt(totalValue - totalInvested, market)} sub="appreciation" />
      </div>
      <div className="space-y-3">
        {data.map((d, i) => (
          <FundCard key={i} icon={Leaf} title={d.name} status={d.status}
            subtitle={[d.type, d.location, d.area].filter(Boolean).join(' · ')}
            metrics={[
              { label: 'Invested',      value: fmt(d.invested || 0, market) },
              { label: 'Current Value', value: fmt(d.current_value || 0, market) },
              ...((d.annual_yield ?? d.annual_yield_pct) != null ? [{ label: 'Annual Yield', value: `${d.annual_yield ?? d.annual_yield_pct}%`, color: 'text-[#ffd879]' }] : []),
              ...(d.irr  != null ? [{ label: 'IRR',  value: `${d.irr}%`,  color: 'text-emerald-600' }] : []),
            ]}
          />
        ))}
      </div>
    </div>
  );
}

// ── Private Others sub-page (10 categories nested in "Others" tab) ───────────

const PRIVATE_OTHER_TABS = [
  { value: 'hedge_fund',         label: 'Hedge Funds',         icon: TrendingUp   },
  { value: 'secondaries',        label: 'Secondaries',         icon: Layers       },
  { value: 'special_situations', label: 'Special Situations',  icon: AlertTriangle },
  { value: 'pre_ipo',            label: 'Pre-IPO',             icon: Rocket       },
  { value: 'mezzanine',          label: 'Mezzanine',           icon: Layers       },
  { value: 'fof',                label: 'Fund of Funds',       icon: LayoutGrid   },
  { value: 'co_investment',      label: 'Co-Investments',      icon: Users        },
  { value: 'royalty',            label: 'Royalty / RBF',       icon: DollarSign   },
  { value: 'natural_resources',  label: 'Natural Resources',   icon: Leaf         },
];

function PrivateOthersTab({ data, market, deals }) {
  const navigate = useNavigate();
  const [activeSub, setActiveSub] = useState('hedge_fund');

  const renderSub = () => {
    switch (activeSub) {
      case 'hedge_fund':         return <HedgeFundTab         data={data.hedge_fund}         market={market} />;
      case 'secondaries':        return <SecondariesTab        data={data.secondaries}        market={market} />;
      case 'special_situations': return <SpecialSituationsTab data={data.special_situations} market={market} />;
      case 'pre_ipo':            return <PreIPOTab            data={data.pre_ipo}            market={market} />;
      case 'mezzanine':          return <MezzanineTab         data={data.mezzanine}          market={market} />;
      case 'fof':                return <FoFTab               data={data.fof}                market={market} />;
      case 'co_investment':      return <CoInvestTab          data={data.co_investment}      market={market} />;
      case 'royalty':            return <RoyaltyTab           data={data.royalty}            market={market} />;
      case 'natural_resources':  return <NaturalResourcesTab  data={data.natural_resources}  market={market} />;
      default:                   return null;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="inline-flex flex-wrap gap-1 p-1 bg-[#f5f5f5] border border-[#e5e5e5] rounded-lg">
          {PRIVATE_OTHER_TABS.map(t => (
            <button
              key={t.value}
              onClick={() => setActiveSub(t.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                activeSub === t.value
                  ? 'bg-[#ffd87a] text-black font-medium'
                  : 'text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-white'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => navigate('/import')}
          className="flex items-center gap-2 px-4 py-2 bg-[#ffd87a] hover:bg-[#f5cc6a] text-black text-sm font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Holdings
        </button>
      </div>
      <DealPipelineSection deals={deals} categories={[activeSub]} market={market} />
      {renderSub()}
    </div>
  );
}

function OverviewTab({ data, deals, market, navigate }) {
  const categories = [
    { key: 'equity',            label: 'PE / VC',              keys: ['vc', 'pe'] },
    { key: 'credit',            label: 'Private Credit',       keys: ['credit'] },
    { key: 'infra',             label: 'Infrastructure',       keys: ['infra'] },
    { key: 'search',            label: 'Search Funds',         keys: ['search'] },
    { key: 'long_only',         label: 'Long-Only Equity',     keys: ['long_only'] },
    { key: 'hedge_fund',        label: 'Hedge Funds',          keys: ['hedge_fund'] },
    { key: 'secondaries',       label: 'Secondaries',          keys: ['secondaries'] },
    { key: 'special_situations',label: 'Special Situations',   keys: ['special_situations'] },
    { key: 'pre_ipo',           label: 'Pre-IPO',              keys: ['pre_ipo'] },
    { key: 'mezzanine',         label: 'Mezzanine Debt',       keys: ['mezzanine'] },
    { key: 'fof',               label: 'Fund of Funds',        keys: ['fof'] },
    { key: 'co_investment',     label: 'Co-Investments',       keys: ['co_investment'] },
    { key: 'royalty',           label: 'Royalty / RBF',        keys: ['royalty'] },
    { key: 'natural_resources', label: 'Natural Resources',    keys: ['natural_resources'] },
    { key: 'others',            label: 'Others',               keys: ['others'] },
  ].map((c, i) => ({ ...c, color: PM_PALETTE[i % PM_PALETTE.length] }));
  const allocation = categories
    .map(c => ({ ...c, value: c.keys.flatMap(k => data[k] || []).reduce((s, d) => s + (d.current_value || d.nav || 0), 0) }))
    .filter(a => a.value > 0);

  const closedDeals = (deals || []).filter(d => d.stage === 'closed');
  const totalStartupsInvested = closedDeals.reduce((s, d) => s + (d.target_investment_amount || 0), 0);
  const startupsEntry = totalStartupsInvested > 0 ? [{ label: 'Startups', value: totalStartupsInvested, color: CHART_PALETTE[5], key: 'startups', keys: [] }] : [];
  const allSegments = [...startupsEntry, ...allocation];
  const grandTotal = allSegments.reduce((s, a) => s + a.value, 0);
  const barData = allSegments.slice(0, 9).map(a => ({ name: a.label.replace('Infrastructure','Infra').replace('Long-Only Equity','Long-Only'), value: a.value, color: a.color }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Stat band */}
      <div style={{ display: 'flex', flexWrap: 'wrap', background: '#fff', border: '1px solid #e5e5e5', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        {[
          { k: 'Startups Invested', v: fmt(totalStartupsInvested, market), delta: `${closedDeals.length} closed deals` },
          ...allocation.slice(0, 4).map(a => ({ k: a.label, v: fmt(a.value, market), delta: `${a.keys.flatMap(k => data[k] || []).length} positions`, color: a.color })),
        ].map((cell, i) => (
          <div key={i} style={{ flex: 1, minWidth: 120, padding: '14px 16px', borderLeft: i > 0 ? '1px solid #e5e5e5' : 'none', textAlign: 'center' }}>
            <div style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, color: '#a8a8a8' }}>{cell.k}</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a', margin: '5px 0 4px', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{cell.v}</div>
            <div style={{ fontSize: 10.5, color: '#888' }}>{cell.delta}</div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, alignItems: 'stretch' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 14 }}>AUM by Strategy</div>
          {barData.length > 0 ? (
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={barData} margin={{ top: 0, right: 0, left: -22, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#888' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: '#aaa' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1e7 ? `${(v/1e7).toFixed(0)}Cr` : v >= 1e5 ? `${(v/1e5).toFixed(0)}L` : v} />
                <Tooltip formatter={(v) => [fmt(v, market), 'AUM']} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e5e5' }} />
                <Bar dataKey="value" radius={[4,4,0,0]}>
                  {barData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{ height: 210, display: 'grid', placeItems: 'center', color: '#aaa', fontSize: 13 }}>No data</div>}
        </div>

        <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 12 }}>Portfolio Split</div>
          {allSegments.length > 0 ? (
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 16, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <PieChart width={150} height={210}>
                  <Pie data={allSegments.slice(0,8).map(a=>({name:a.label,value:a.value,fill:a.color}))} cx={75} cy={105} innerRadius={52} outerRadius={72} dataKey="value" paddingAngle={2}>
                    {allSegments.slice(0,8).map((a, i) => <Cell key={i} fill={a.color} />)}
                  </Pie>
                </PieChart>
              </div>
              <ul style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0 }}>
                {allSegments.slice(0, 7).map((a, i) => {
                  const pct = grandTotal > 0 ? (a.value / grandTotal) * 100 : 0;
                  return (
                    <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: i < allSegments.slice(0,7).length - 1 ? '1px solid #f0f0f0' : 'none', fontSize: 10.5 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: a.color, flexShrink: 0 }} />
                      <span style={{ flex: 1, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.label}</span>
                      <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{pct < 0.1 ? '<0.1' : pct.toFixed(1)}%</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : <div style={{ flex: 1, display: 'grid', placeItems: 'center', color: '#aaa', fontSize: 12 }}>No data</div>}
        </div>
      </div>

      {/* Deal pipeline */}
      <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#a8a8a8', marginBottom: 12 }}>Live Deal Pipeline</div>
        {deals.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#aaa', fontSize: 12 }}>No deals in pipeline yet</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
            {deals.slice(0, 6).map((d, i) => (
              <div key={i} onClick={() => navigate('?tab=startups')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#fafafa', borderRadius: 10, border: '1px solid #f0f0f0', cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s' }} onMouseEnter={e => { e.currentTarget.style.borderColor = '#ffd87a'; e.currentTarget.style.background = '#fffdf5'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = '#f0f0f0'; e.currentTarget.style.background = '#fafafa'; }}>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1a1a1a' }}>{d.deal_name || d.company_name}</div>
                  <div style={{ fontSize: 10.5, color: '#888', marginTop: 2 }}>{d.sector}</div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 99, background: d.stage === 'closed' ? 'rgba(16,185,129,0.12)' : d.stage === 'passed' ? 'rgba(239,68,68,0.1)' : 'rgba(255,216,121,0.3)', color: d.stage === 'closed' ? '#059669' : d.stage === 'passed' ? '#e53535' : '#7a6200' }}>
                  {d.stage?.replace('_', ' ')}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PrivateMarketsDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const market = user?.market || 'IN';

  const activeTab = searchParams.get('tab') || 'overview';
  const handleTabChange = (tab) => setSearchParams({ tab });

  const CATEGORIES = [
    'vc', 'pe', 'search', 'long_only', 'credit', 'infra',
    'hedge_fund', 'secondaries', 'special_situations', 'pre_ipo',
    'mezzanine', 'fof', 'co_investment', 'royalty', 'natural_resources',
  ];

  const [data, setData] = useState(Object.fromEntries(CATEGORIES.map(c => [c, []])));
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      ...CATEGORIES.map(c => axios.get(`${API}/private-markets/${c}`).then(r => [c, r.data || []]).catch(() => [c, []])),
      axios.get(`${API}/deals/`).then(r => ['deals', r.data || []]).catch(() => ['deals', []]),
    ]).then(results => {
      const next = Object.fromEntries(CATEGORIES.map(c => [c, []]));
      let dealsData = [];
      for (const [key, val] of results) {
        if (key === 'deals') dealsData = val;
        else next[key] = val;
      }
      setData(next);
      setDeals(dealsData);
    }).finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const totalAUM = Object.values(data).flat().reduce((s, d) => s + (d.current_value || d.nav || 0), 0);

  const TC = 'text-[#4a4a4a] data-[state=active]:bg-[#ffd87a] data-[state=active]:text-black gap-1.5';

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div style={{ display: 'flex', gap: 18, alignItems: 'center' }}>
          <div style={{ width: 46, height: 46, borderRadius: 14, background: '#dae4c4', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Layers size={22} color="#1a1a1a" />
          </div>
          <div>
            <div style={{ textTransform: 'uppercase', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.13em', color: '#a8a8a8', marginBottom: 4 }}>Asset Dashboard</div>
            <h1 style={{ fontFamily: "'PerfectNineties', Georgia, serif", fontWeight: 500, fontSize: 34, letterSpacing: '-0.02em', lineHeight: 1.05, color: '#2a2a2a', margin: 0 }}>Private Markets</h1>
          </div>
        </div>
        {activeTab !== 'startups' && (
          <button
            onClick={() => navigate('/import')}
            className="flex items-center gap-2 px-4 py-2 bg-[#ffd87a] hover:bg-[#f5cc6a] text-black text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Holdings
          </button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="bg-[#f5f5f5] border border-[#e5e5e5] mb-6 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="overview"  className={TC}><TrendingUp     className="w-3.5 h-3.5" /> Overview</TabsTrigger>
          <TabsTrigger value="startups"  className={TC}><Rocket         className="w-3.5 h-3.5" /> Startups</TabsTrigger>
          <TabsTrigger value="equity"    className={TC}><Building2      className="w-3.5 h-3.5" /> PE / VC</TabsTrigger>
          <TabsTrigger value="search"    className={TC}><Search         className="w-3.5 h-3.5" /> Search Funds</TabsTrigger>
          <TabsTrigger value="long_only" className={TC}><TrendingUp     className="w-3.5 h-3.5" /> Long-Only</TabsTrigger>
          <TabsTrigger value="credit"    className={TC}><DollarSign     className="w-3.5 h-3.5" /> Private Credit</TabsTrigger>
          <TabsTrigger value="infra"     className={TC}><Zap            className="w-3.5 h-3.5" /> Infrastructure</TabsTrigger>
          <TabsTrigger value="others"    className={TC}><MoreHorizontal className="w-3.5 h-3.5" /> Others</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">  <OverviewTab data={data} deals={deals} market={market} navigate={navigate} /></TabsContent>
        <TabsContent value="startups">  <DealsPage embedded /></TabsContent>
        <TabsContent value="equity">    <EquityTab vc={data.vc} pe={data.pe} market={market} deals={deals} /></TabsContent>
        <TabsContent value="search">    <GenericFundTab data={data.search}    market={market} icon={Search}     emptyLabel="search funds"     category="search"    deals={deals} /></TabsContent>
        <TabsContent value="long_only"> <GenericFundTab data={data.long_only} market={market} icon={TrendingUp} emptyLabel="long-only equity" category="long_only" deals={deals} /></TabsContent>
        <TabsContent value="credit">    <CreditTab data={data.credit} market={market} deals={deals} /></TabsContent>
        <TabsContent value="infra">     <InfraTab  data={data.infra}  market={market} deals={deals} /></TabsContent>
        <TabsContent value="others">    <PrivateOthersTab data={data} market={market} deals={deals} /></TabsContent>
      </Tabs>
    </div>
  );
}
